/**
 * MIDWAY AUTH GATE — holds app rendering until the Midway session flow
 * resolves (spec midway-client-auth R1).
 *
 * On a deployed stage the app must never render unauthenticated: the header
 * would show the placeholder identity and every API call would fail. The gate
 * runs ensureMidwaySession() once (memoized in the accessor, so the
 * StrictMode double-mount is harmless) and:
 *  - shows a centered spinner while the silent check runs;
 *  - keeps the spinner when the result is 'redirecting' (the page is
 *    navigating to Midway login — rendering anything else would flash);
 *  - renders children on 'authenticated', 'skipped' (standalone dev server),
 *    or 'unavailable' (a login redirect already failed this browsing session
 *    — fall open, do not loop), and also if the flow ever rejects — fail-open,
 *    because the middleware's own authorizer stays the enforcement point
 *    (R1.5, R1.6);
 *  - renders a sign-in prompt on 'sign-in-required' (embedded, no session):
 *    the frame cannot navigate to Midway, so the user signs in at top level
 *    (new tab) and reloads the host page.
 */

import { ReactNode, useEffect, useState } from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Spinner from '@amzn/awsui-components-core/spinner';

import { ensureMidwaySession } from 'src/accessors/midwayAuth';

interface MidwayAuthGateProps {
  children: ReactNode;
}

type GateState = 'pending' | 'app' | 'sign-in';

/**
 * This page's own URL with the `embed` override removed, so the new tab gets
 * the standalone shell (frame detection alone yields standalone in a tab).
 */
export function standaloneUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete('embed');
  return url.toString();
}

const EmbeddedSignInPrompt = () => (
  <div style={{ padding: '24px' }}>
    <Alert type="warning" header="Sign in required to view this dashboard here">
      A Midway sign-in cannot be completed inside this panel.{' '}
      <a
        href={standaloneUrl(window.location.href)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Sign in to Midway in a new tab
      </a>
      , then reload this page. If this keeps happening, your browser is
      blocking third-party cookies; use that new tab to view the dashboard.
    </Alert>
  </div>
);

export const MidwayAuthGate = ({ children }: MidwayAuthGateProps) => {
  const [state, setState] = useState<GateState>('pending');

  useEffect(() => {
    let cancelled = false;
    ensureMidwaySession()
      .then((result) => {
        if (cancelled) return;
        // 'redirecting' keeps the spinner: window.location is already moving.
        if (result === 'redirecting') return;
        setState(result === 'sign-in-required' ? 'sign-in' : 'app');
      })
      .catch(() => {
        if (!cancelled) setState('app'); // Fail-open (R1.5).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'pending') {
    return (
      <div
        role="status"
        aria-label="Signing in with Midway"
        style={{ display: 'flex', justifyContent: 'center', padding: '20vh 0' }}
      >
        <Spinner size="large" />
      </div>
    );
  }
  if (state === 'sign-in') return <EmbeddedSignInPrompt />;
  return <>{children}</>;
};
